import { useMutation } from '@apollo/client';
import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { InsertIssueUpdateMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertIssueUpdateDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import { useInsertIssueUpdateTRPC } from './useInsertIssueUpdateTRPC';

type InsertIssueUpdateInput = {
  ParentIssueId: string;
  Title: string;
  Description: string;
  CustomAttributeData?: CustomAttributeData | null;
};

export const useInsertIssueUpdate = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [insertIssueUpdateGraphQL, graphqlState] = useMutation(
    InsertIssueUpdateDocument,
    {}
  );

  // tRPC mutation
  const trpcMutation = useInsertIssueUpdateTRPC();

  const insertIssueUpdate = async (
    variables: InsertIssueUpdateInput
  ): Promise<InsertIssueUpdateMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertIssueUpdate(variables);
    }

    const result = await insertIssueUpdateGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to insert issue update');
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      insertIssueUpdate,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertIssueUpdate,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
